import { useCallback, useEffect, useMemo, useState } from 'react';
import { GitAdminIssue, GitAdminRepository, GitIssueRefreshResult, GitIssueTimeline, GitRefreshRequest } from '../../../interfaces';

type AppsScope = 'central' | 'my' | '';

type TimePoint = {
  timestamp: number;
  value: number;
};

type TimelineMeta = GitIssueTimeline;

type TimelineGraph = {
  spentPath: string;
  estimatePath: string;
  minTs: number;
  maxTs: number;
  estimate: number;
  spent: number;
  maxY: number;
};

type TimelineDebugPayload = {
  request: GitRefreshRequest & { projectId: number; issueIid: number };
  response: GitIssueTimeline;
};

type UseGitAdminTimelineParams = {
  issues: GitAdminIssue[];
  setIssues: React.Dispatch<React.SetStateAction<GitAdminIssue[]>>;
  repositoriesById: Map<number, GitAdminRepository>;
  gitToken: string;
  gitLabApiBaseUrl: string;
  showAppsTimeline: boolean;
  getIssueTimeEvents: (request: { gitLabApiBaseUrl: string; gitToken: string; projectId: number; issueIid: number }) => Promise<GitIssueTimeline>;
  refreshIssueApp: (request: { gitLabApiBaseUrl: string; gitToken: string; projectId: number; issueIid: number }) => Promise<GitIssueRefreshResult>;
  fetchPagedList: <T>(
    fetchPageResponse: (index: number, nbItems: number) => Promise<{ result: T[]; total: number }>,
    onFirstPage?: (items: T[]) => void
  ) => Promise<T[]>;
  getIssues: (query: { index: number; nbItems: number; descOrder: boolean }) => Promise<{ result: GitAdminIssue[]; total: number }>;
};

const TIMELINE_CHART_LEFT = 10;
const TIMELINE_CHART_RIGHT = 98;
const TIMELINE_CHART_TOP = 8;
const TIMELINE_CHART_BOTTOM = 92;

const parseLabels = (labelsJson?: string | null): string[] => {
  if (!labelsJson) {
    return [];
  }

  try {
    const parsed = JSON.parse(labelsJson) as unknown;
    return Array.isArray(parsed) ? parsed.filter((label): label is string => typeof label === 'string') : [];
  } catch {
    return [];
  }
};

const buildProgressPath = (points: TimePoint[], minTs: number, maxTs: number, maxY: number): string => {
  if (points.length === 0) {
    return '';
  }

  const safeRange = Math.max(1, maxTs - minTs);
  const safeMaxY = Math.max(1, maxY);
  const chartWidth = TIMELINE_CHART_RIGHT - TIMELINE_CHART_LEFT;
  const chartHeight = TIMELINE_CHART_BOTTOM - TIMELINE_CHART_TOP;

  if (points.length === 1) {
    const onlyPoint = points[0];
    const x = TIMELINE_CHART_LEFT + ((onlyPoint.timestamp - minTs) / safeRange) * chartWidth;
    const y = TIMELINE_CHART_BOTTOM - (Math.min(onlyPoint.value, safeMaxY) / safeMaxY) * chartHeight;
    const x2 = Math.min(TIMELINE_CHART_RIGHT, x + 0.6);
    return `M ${x.toFixed(2)} ${y.toFixed(2)} L ${x2.toFixed(2)} ${y.toFixed(2)}`;
  }

  return points.map((point, index) => {
    const x = TIMELINE_CHART_LEFT + ((point.timestamp - minTs) / safeRange) * chartWidth;
    const y = TIMELINE_CHART_BOTTOM - (Math.min(point.value, safeMaxY) / safeMaxY) * chartHeight;
    return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ');
};

const toValidTimestamp = (value: number, fallback: number = Date.now()): number =>
  Number.isFinite(value) && value > 0 ? value : fallback;

const parseApiTimestamp = (value?: string | null): number => {
  if (!value) {
    return Number.NaN;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return Number.NaN;
  }

  const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(trimmed);
  const withTimezone = hasTimezone ? trimmed : `${trimmed}Z`;

  const normalized = withTimezone.replace(/\.(\d{3})\d+(?=(?:Z|[+-]\d{2}:\d{2})$)/i, '.$1');
  const parsed = Date.parse(normalized);
  if (Number.isFinite(parsed)) {
    return parsed;
  }

  const fallback = Date.parse(trimmed);
  return Number.isFinite(fallback) ? fallback : Number.NaN;
};

const normalizeCumulativeSeries = (points: TimePoint[]): TimePoint[] => {
  let running = 0;
  return points.map(point => {
    running = Math.max(running, Math.max(0, point.value));
    return {
      timestamp: point.timestamp,
      value: running
    };
  });
};

const formatApiRefreshDate = (value?: string): string | null => {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return new Date(parsed).toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'medium'
  });
};

const useGitAdminTimeline = ({
  issues,
  setIssues,
  repositoriesById,
  gitToken,
  gitLabApiBaseUrl,
  showAppsTimeline,
  getIssueTimeEvents,
  refreshIssueApp,
  fetchPagedList,
  getIssues
}: UseGitAdminTimelineParams) => {
  const [appsScope, setAppsScope] = useState<AppsScope>('');
  const [selectedAppsIssueId, setSelectedAppsIssueId] = useState<number | null>(null);
  const [appsTimePoints, setAppsTimePoints] = useState<TimePoint[]>([]);
  const [appsEstimatePoints, setAppsEstimatePoints] = useState<TimePoint[]>([]);
  const [appsTimelineMeta, setAppsTimelineMeta] = useState<TimelineMeta | null>(null);
  const [appsTimelineRefreshNonce, setAppsTimelineRefreshNonce] = useState(0);
  const [appsTimelineDebug, setAppsTimelineDebug] = useState<TimelineDebugPayload | null>(null);
  const [appsTimeEventsLoading, setAppsTimeEventsLoading] = useState(false);
  const [appsTimeEventsError, setAppsTimeEventsError] = useState<string | null>(null);
  const [appsIssueRefreshLoading, setAppsIssueRefreshLoading] = useState(false);
  const [appsIssueRefreshMessage, setAppsIssueRefreshMessage] = useState<string | null>(null);

  const appsIssues = useMemo(() => {
    if (!appsScope) {
      return [];
    }

    return issues.filter(issue => {
      const repository = repositoriesById.get(issue.gitRepositoryId);
      if (!repository) {
        return false;
      }

      const projectPath = `${(repository.namespace ?? '').trim()}/${(repository.name ?? '').trim()}`.toLowerCase();
      return projectPath === `hobud/${appsScope}/apps`;
    });
  }, [issues, repositoriesById, appsScope]);

  const selectedAppsIssue = useMemo(
    () => appsIssues.find(issue => issue.id === selectedAppsIssueId) ?? null,
    [appsIssues, selectedAppsIssueId]
  );

  const refreshAppsTimeline = useCallback(() => {
    setAppsTimelineRefreshNonce(value => value + 1);
  }, []);

  const refreshSelectedAppsIssue = useCallback(async () => {
    if (!selectedAppsIssue || !gitToken) {
      return;
    }

    const repository = repositoriesById.get(selectedAppsIssue.gitRepositoryId);
    if (!repository) {
      setAppsIssueRefreshMessage('Repository introuvable pour cette issue.');
      return;
    }

    setAppsIssueRefreshLoading(true);
    setAppsIssueRefreshMessage(null);

    try {
      const result = await refreshIssueApp({
        gitLabApiBaseUrl,
        gitToken,
        projectId: repository.gitlabProjectId,
        issueIid: selectedAppsIssue.gitlabIssueIid
      });

      const refreshedIssues = await fetchPagedList(
        (index, nbItems) => getIssues({ index, nbItems, descOrder: false })
      );
      setIssues(refreshedIssues);

      const refreshedAtLabel = formatApiRefreshDate(result.lastRefreshedAtUtc);
      const refreshSuffix = refreshedAtLabel ? ` Dernier rafraichissement API: ${refreshedAtLabel}.` : '';
      setAppsIssueRefreshMessage(`Issue rafraichie: ${result.issuesUpdated} issue(s), ${result.childrenLinked} tâche(s) liée(s), ${result.snapshotsLogged} snapshot(s).${refreshSuffix}`);
      setAppsTimelineRefreshNonce(value => value + 1);
    } catch (refreshError) {
      const message = refreshError instanceof Error ? refreshError.message : 'Impossible de rafraichir cette issue.';
      setAppsIssueRefreshMessage(message);
    } finally {
      setAppsIssueRefreshLoading(false);
    }
  }, [selectedAppsIssue, gitToken, repositoriesById, refreshIssueApp, gitLabApiBaseUrl, fetchPagedList, getIssues, setIssues]);

  useEffect(() => {
    if (showAppsTimeline && selectedAppsIssue) {
      setAppsTimelineRefreshNonce(value => value + 1);
    }
  }, [showAppsTimeline, selectedAppsIssue]);

  useEffect(() => {
    if (!showAppsTimeline || !selectedAppsIssue || !gitToken) {
      setAppsTimePoints([]);
      setAppsEstimatePoints([]);
      setAppsTimelineMeta(null);
      if (!showAppsTimeline || !selectedAppsIssue) {
        setAppsTimelineDebug(null);
      }
      setAppsTimeEventsError(null);
      return;
    }

    const repository = repositoriesById.get(selectedAppsIssue.gitRepositoryId);
    if (!repository) {
      setAppsTimePoints([]);
      setAppsEstimatePoints([]);
      setAppsTimelineMeta(null);
      setAppsTimeEventsError('Repository introuvable pour cette issue.');
      return;
    }

    let cancelled = false;
    const fetchTimeEvents = async () => {
      setAppsTimeEventsLoading(true);
      setAppsTimeEventsError(null);

      try {
        const requestPayload = {
          gitLabApiBaseUrl,
          gitToken,
          projectId: repository.gitlabProjectId,
          issueIid: selectedAppsIssue.gitlabIssueIid
        };
        const timeline = await getIssueTimeEvents(requestPayload);
        if (cancelled) {
          return;
        }

        setAppsTimelineDebug({
          request: {
            ...requestPayload,
            gitToken: '***masked***'
          },
          response: timeline
        });

        const dailySpentPoints = (timeline.dailyTimeline ?? [])
          .map(point => ({
            timestamp: parseApiTimestamp(point.at ?? null),
            value: Math.max(0, point.totalTimeSpentSeconds ?? 0)
          }))
          .filter(point => Number.isFinite(point.timestamp) && point.timestamp > 0)
          .sort((left, right) => left.timestamp - right.timestamp);

        const dailyEstimatePoints = (timeline.dailyTimeline ?? [])
          .map(point => ({
            timestamp: parseApiTimestamp(point.at ?? null),
            value: Math.max(0, point.totalTimeEstimateSeconds ?? 0)
          }))
          .filter(point => Number.isFinite(point.timestamp) && point.timestamp > 0)
          .sort((left, right) => left.timestamp - right.timestamp);

        if (dailySpentPoints.length > 0 || dailyEstimatePoints.length > 0) {
          setAppsTimePoints(dailySpentPoints);
          setAppsEstimatePoints(dailyEstimatePoints);
          setAppsTimelineMeta({
            createdAt: timeline.createdAt,
            totalTimeSpentSeconds: Math.max(0, timeline.totalTimeSpentSeconds ?? 0),
            totalTimeEstimateSeconds: Math.max(0, timeline.totalTimeEstimateSeconds ?? 0),
            events: timeline.events ?? [],
            estimateEvents: timeline.estimateEvents ?? [],
            refreshSnapshots: timeline.refreshSnapshots ?? [],
            dailyTimeline: timeline.dailyTimeline ?? [],
            relatedIssues: timeline.relatedIssues ?? []
          });
          return;
        }

        const sorted = [...(timeline.events ?? [])].sort((left, right) => {
          const leftDate = parseApiTimestamp(left.spentAt ?? left.createdAt ?? null);
          const rightDate = parseApiTimestamp(right.spentAt ?? right.createdAt ?? null);
          return leftDate - rightDate;
        });

        const snapshotSpentPoints = (timeline.refreshSnapshots ?? [])
          .map(snapshot => ({
            timestamp: parseApiTimestamp(snapshot.at ?? null),
            value: Math.max(0, snapshot.totalTimeSpentSeconds ?? 0)
          }))
          .filter(point => Number.isFinite(point.timestamp) && point.timestamp > 0)
          .sort((left, right) => left.timestamp - right.timestamp);

        const snapshotEstimatePoints = (timeline.refreshSnapshots ?? [])
          .map(snapshot => ({
            timestamp: parseApiTimestamp(snapshot.at ?? null),
            value: Math.max(0, snapshot.totalTimeEstimateSeconds ?? 0)
          }))
          .filter(point => Number.isFinite(point.timestamp) && point.timestamp > 0)
          .sort((left, right) => left.timestamp - right.timestamp);

        const maxPointValue = (points: TimePoint[]): number =>
          points.length > 0 ? Math.max(0, ...points.map(point => point.value)) : 0;

        let cumulativeSpent = 0;
        let spentPoints: TimePoint[] = [];
        let spentUsesSnapshots = false;
        sorted.forEach(event => {
          const eventTimestamp = parseApiTimestamp(event.spentAt ?? event.createdAt ?? null);
          if (!Number.isFinite(eventTimestamp) || eventTimestamp <= 0) {
            return;
          }

          cumulativeSpent += event.timeToSeconds ?? 0;
          spentPoints.push({ timestamp: eventTimestamp, value: Math.max(0, cumulativeSpent) });
        });

        const issueCreatedAtTs = parseApiTimestamp(timeline.createdAt ?? selectedAppsIssue.createdAt ?? null);
        if (spentPoints.length > 0 && Number.isFinite(issueCreatedAtTs) && issueCreatedAtTs > 0) {
          spentPoints.unshift({ timestamp: issueCreatedAtTs, value: 0 });
        }

        const eventSpentMax = maxPointValue(spentPoints);
        const snapshotSpentMax = maxPointValue(snapshotSpentPoints);
        if (snapshotSpentMax > 0 && eventSpentMax === 0) {
          spentPoints = snapshotSpentPoints;
          spentUsesSnapshots = true;
        }

        if (spentPoints.length === 0) {
          const fallbackStart = toValidTimestamp(issueCreatedAtTs);
          const fallbackEnd = toValidTimestamp(parseApiTimestamp(selectedAppsIssue.updatedAt ?? null), fallbackStart);
          const spent = Math.max(0, timeline.totalTimeSpentSeconds ?? selectedAppsIssue.timeSpentSeconds ?? 0);
          spentPoints.push({ timestamp: fallbackStart, value: 0 });
          spentPoints.push({ timestamp: fallbackEnd, value: spent });
        }

        let estimatePoints = [...(timeline.estimateEvents ?? [])]
          .map(event => ({
            timestamp: parseApiTimestamp(event.at ?? null),
            value: Math.max(0, event.totalEstimateSeconds ?? 0)
          }))
          .filter(point => Number.isFinite(point.timestamp) && point.timestamp > 0)
          .sort((left, right) => left.timestamp - right.timestamp);
        let estimateUsesSnapshots = false;

        const eventEstimateMax = maxPointValue(estimatePoints);
        const snapshotEstimateMax = maxPointValue(snapshotEstimatePoints);
        if (snapshotEstimateMax > 0 && eventEstimateMax === 0) {
          estimatePoints = snapshotEstimatePoints;
          estimateUsesSnapshots = true;
        }

        if (estimatePoints.length === 0) {
          const fallbackTs = Number.isFinite(issueCreatedAtTs) && issueCreatedAtTs > 0
            ? issueCreatedAtTs
            : toValidTimestamp(parseApiTimestamp(selectedAppsIssue.updatedAt ?? null));
          estimatePoints.push({
            timestamp: fallbackTs,
            value: Math.max(0, timeline.totalTimeEstimateSeconds ?? selectedAppsIssue.timeEstimateSeconds ?? 0)
          });
        }

        if (Number.isFinite(issueCreatedAtTs) && issueCreatedAtTs > 0) {
          if (spentUsesSnapshots && spentPoints.length > 0 && spentPoints[0].timestamp > issueCreatedAtTs) {
            spentPoints.unshift({ timestamp: issueCreatedAtTs, value: 0 });
          }

          if (estimateUsesSnapshots && estimatePoints.length > 0 && estimatePoints[0].timestamp > issueCreatedAtTs) {
            estimatePoints.unshift({ timestamp: issueCreatedAtTs, value: 0 });
          }
        }

        setAppsTimePoints(spentPoints);
        setAppsEstimatePoints(estimatePoints);
        setAppsTimelineMeta({
          createdAt: timeline.createdAt,
          totalTimeSpentSeconds: Math.max(0, timeline.totalTimeSpentSeconds ?? 0),
          totalTimeEstimateSeconds: Math.max(0, timeline.totalTimeEstimateSeconds ?? 0),
          events: timeline.events ?? [],
          estimateEvents: timeline.estimateEvents ?? [],
          refreshSnapshots: timeline.refreshSnapshots ?? [],
          dailyTimeline: timeline.dailyTimeline ?? [],
          relatedIssues: timeline.relatedIssues ?? []
        });
      } catch (fetchError) {
        if (!cancelled) {
          const message = fetchError instanceof Error ? fetchError.message : 'Impossible de charger la timeline.';
          setAppsTimeEventsError(message);
          setAppsTimelineDebug(null);
          setAppsTimePoints([]);
          setAppsEstimatePoints([]);
          setAppsTimelineMeta(null);
        }
      } finally {
        if (!cancelled) {
          setAppsTimeEventsLoading(false);
        }
      }
    };

    fetchTimeEvents().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [showAppsTimeline, selectedAppsIssue, gitToken, gitLabApiBaseUrl, repositoriesById, getIssueTimeEvents, appsTimelineRefreshNonce]);

  useEffect(() => {
    if (selectedAppsIssueId !== null && !appsIssues.some(issue => issue.id === selectedAppsIssueId)) {
      setSelectedAppsIssueId(null);
    }
  }, [appsIssues, selectedAppsIssueId]);

  const relatedAppsIssues = useMemo(() => {
    if (!selectedAppsIssue) {
      return [];
    }

    const issueRelations = issues.filter(issue => issue.parentGitIssueId === selectedAppsIssue.id || selectedAppsIssue.parentGitIssueId === issue.id);
    if (issueRelations.length > 0) {
      return issueRelations.map(issue => ({
        id: issue.id,
        projectId: issue.gitRepositoryId,
        iid: issue.gitlabIssueIid,
        createdAt: issue.createdAt,
        updatedAt: issue.updatedAt,
        webUrl: issue.webUrl,
        title: issue.title,
        state: issue.state,
        assigneeName: issue.assigneeName,
        timeSpentSeconds: issue.timeSpentSeconds ?? 0,
        timeEstimateSeconds: issue.timeEstimateSeconds ?? 0,
        labels: parseLabels(issue.labelsJson)
      }));
    }

    return appsTimelineMeta?.relatedIssues ?? [];
  }, [issues, appsTimelineMeta, selectedAppsIssue]);

  const relatedTotals = useMemo(() => {
    return relatedAppsIssues.reduce(
      (accumulator, issue) => ({
        spent: accumulator.spent + Math.max(0, issue.timeSpentSeconds ?? 0),
        estimate: accumulator.estimate + Math.max(0, issue.timeEstimateSeconds ?? 0)
      }),
      { spent: 0, estimate: 0 }
    );
  }, [relatedAppsIssues]);

  const appsTimelineGraph = useMemo((): TimelineGraph | null => {
    if (!selectedAppsIssue) {
      return null;
    }

    const maxPointValue = (points: TimePoint[]): number =>
      points.length > 0 ? Math.max(0, ...points.map(point => point.value)) : 0;

    const timelineSpent = Math.max(0, appsTimelineMeta?.totalTimeSpentSeconds ?? 0);
    const timelineEstimate = Math.max(0, appsTimelineMeta?.totalTimeEstimateSeconds ?? 0);
    const snapshotFallbackSpent = Math.max(0, ...(appsTimelineMeta?.refreshSnapshots ?? []).map(snapshot => Math.max(0, snapshot.totalTimeSpentSeconds ?? 0)));
    const snapshotFallbackEstimate = Math.max(0, ...(appsTimelineMeta?.refreshSnapshots ?? []).map(snapshot => Math.max(0, snapshot.totalTimeEstimateSeconds ?? 0)));
    const ownSpent = Math.max(0, selectedAppsIssue.timeSpentSeconds ?? 0);
    const ownEstimate = Math.max(0, selectedAppsIssue.timeEstimateSeconds ?? 0);

    const fallbackSpent = timelineSpent > 0 ? timelineSpent : Math.max(snapshotFallbackSpent, ownSpent, relatedTotals.spent);
    const fallbackEstimate = timelineEstimate > 0 ? timelineEstimate : Math.max(snapshotFallbackEstimate, ownEstimate, relatedTotals.estimate);
    const fallbackTs = toValidTimestamp(parseApiTimestamp(selectedAppsIssue.updatedAt ?? null));
    let spentBasePoints = (appsTimePoints.length > 0
      ? appsTimePoints
      : [{ timestamp: fallbackTs, value: fallbackSpent }])
      .filter(point => Number.isFinite(point.timestamp) && point.timestamp > 0)
      .sort((left, right) => left.timestamp - right.timestamp);

    let estimateBasePoints = (appsEstimatePoints.length > 0
      ? appsEstimatePoints
      : [{ timestamp: fallbackTs, value: fallbackEstimate }])
      .filter(point => Number.isFinite(point.timestamp) && point.timestamp > 0)
      .sort((left, right) => left.timestamp - right.timestamp);

    if (maxPointValue(spentBasePoints) === 0 && fallbackSpent > 0) {
      spentBasePoints = [{ timestamp: fallbackTs, value: fallbackSpent }];
    }

    if (maxPointValue(estimateBasePoints) === 0 && fallbackEstimate > 0) {
      estimateBasePoints = [{ timestamp: fallbackTs, value: fallbackEstimate }];
    }

    if (spentBasePoints.length === 0 && estimateBasePoints.length === 0) {
      return null;
    }

    const todayTs = Date.now();
    const spentPoints = normalizeCumulativeSeries([...spentBasePoints]);
    const estimatePoints = normalizeCumulativeSeries([...estimateBasePoints]);

    const lastSpentPoint = spentPoints[spentPoints.length - 1];
    if (lastSpentPoint.timestamp < todayTs) {
      spentPoints.push({ timestamp: todayTs, value: lastSpentPoint.value });
    }

    const lastEstimatePoint = estimatePoints[estimatePoints.length - 1];
    if (lastEstimatePoint.timestamp < todayTs) {
      estimatePoints.push({ timestamp: todayTs, value: lastEstimatePoint.value });
    }

    const maxSpent = spentPoints.length > 0 ? Math.max(0, ...spentPoints.map(point => point.value)) : 0;
    const maxEstimate = estimatePoints.length > 0 ? Math.max(0, ...estimatePoints.map(point => point.value)) : 0;
    const maxY = Math.max(maxSpent, maxEstimate, 1);

    const allTimestamps = [
      ...spentPoints.map(point => point.timestamp),
      ...estimatePoints.map(point => point.timestamp)
    ];
    const minTs = Math.min(...allTimestamps);
    let maxTs = Math.max(
      todayTs,
      ...allTimestamps
    );
    if (maxTs <= minTs) {
      maxTs = minTs + 1;
    }

    const spentPath = spentPoints.length > 0 ? buildProgressPath(spentPoints, minTs, maxTs, maxY) : '';
    const estimatePath = estimatePoints.length > 0 ? buildProgressPath(estimatePoints, minTs, maxTs, maxY) : '';

    const currentEstimate = Math.max(estimatePoints[estimatePoints.length - 1]?.value ?? 0, fallbackEstimate);
    const currentSpent = Math.max(spentPoints[spentPoints.length - 1]?.value ?? 0, fallbackSpent);

    return {
      spentPath,
      estimatePath,
      minTs,
      maxTs,
      estimate: currentEstimate,
      spent: currentSpent,
      maxY
    };
  }, [selectedAppsIssue, appsTimePoints, appsEstimatePoints, appsTimelineMeta, relatedTotals.spent, relatedTotals.estimate]);

  return {
    appsScope,
    setAppsScope,
    selectedAppsIssueId,
    setSelectedAppsIssueId,
    appsIssues,
    selectedAppsIssue,
    appsTimeEventsLoading,
    appsTimeEventsError,
    appsIssueRefreshLoading,
    appsIssueRefreshMessage,
    appsTimelineDebug,
    appsTimelineGraph,
    relatedAppsIssues,
    refreshAppsTimeline,
    refreshSelectedAppsIssue
  };
};

export default useGitAdminTimeline;
